// Integrations landing — Phase 1 follow-up to the Wave D QB build.
//
// Card grid for outbound integrations. For now there's one live card
// (QuickBooks Online) plus a muted placeholder for future integrations.
//
// Visibility rule: route is staff-readable so the page itself is never
// 403'd. Each card self-locks when the current user lacks admin role —
// click is suppressed and a tooltip reads "Admin access required". This
// matches the pattern used by /admin and /settings landings, where the
// page renders for everyone but mutating actions are admin-gated.

import { Link } from 'react-router-dom';
import { CheckCircle, Lock, Plug, Sparkles, XCircle } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Tooltip } from '../../../components/ui/Tooltip/Tooltip';
import { useAuthStore } from '../../../store/authStore';
import { useQbStatus } from './quickbooks/hooks/useQuickBooks';
import styles from './IntegrationsLandingPage.module.css';

export function IntegrationsLandingPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const isAdmin = role === 'admin';
  // Connection state is the same across modules; query the default.
  const status = useQbStatus('collections');
  const connected = status.data?.connected ?? false;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Integrations"
        subtitle="Connect Tricivo to outside accounting and reporting tools."
      />

      <Card title="Available integrations">
        <ul className={styles.grid}>
          <li>
            <IntegrationCard
              to="/collections/integrations/quickbooks"
              icon={<Plug size={20} aria-hidden />}
              label="QuickBooks Online"
              description="Sync paid receipts to QuickBooks as expenses with attached PDFs."
              chip={
                connected ? (
                  <Badge variant="success" icon={<CheckCircle size={12} aria-hidden />}>
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="muted" icon={<XCircle size={12} aria-hidden />}>
                    Not connected
                  </Badge>
                )
              }
              locked={!isAdmin}
            />
          </li>
          <li>
            <DisabledCard
              icon={<Sparkles size={20} aria-hidden />}
              label="More integrations coming soon"
              description="Xero, Sage, Stripe and others will appear here as they ship."
            />
          </li>
        </ul>
      </Card>
    </div>
  );
}

interface IntegrationCardProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  chip?: React.ReactNode;
  locked: boolean;
}

function IntegrationCard({ to, icon, label, description, chip, locked }: IntegrationCardProps): JSX.Element {
  if (locked) {
    return (
      <Tooltip content="Admin access required">
        <span className={`${styles.card} ${styles.locked}`} aria-disabled="true">
          <span className={styles.icon}>{icon}</span>
          <span className={styles.text}>
            <span className={styles.labelRow}>
              <span className={styles.label}>{label}</span>
              <Lock size={14} aria-hidden />
            </span>
            <span className={styles.description}>{description}</span>
            {chip ? <span className={styles.chip}>{chip}</span> : null}
          </span>
        </span>
      </Tooltip>
    );
  }
  return (
    <Link to={to} className={styles.card}>
      <span className={styles.icon}>{icon}</span>
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        <span className={styles.description}>{description}</span>
        {chip ? <span className={styles.chip}>{chip}</span> : null}
      </span>
    </Link>
  );
}

interface DisabledCardProps {
  icon: React.ReactNode;
  label: string;
  description: string;
}

function DisabledCard({ icon, label, description }: DisabledCardProps): JSX.Element {
  return (
    <span className={`${styles.card} ${styles.placeholder}`} aria-disabled="true">
      <span className={styles.icon}>{icon}</span>
      <span className={styles.text}>
        <span className={styles.label}>{label}</span>
        <span className={styles.description}>{description}</span>
      </span>
    </span>
  );
}
